import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
} from '@mui/material';
import { useNavigate, Link } from 'react-router-dom';
import Popup from '../../base-components/Popup';
import { PageHeader } from '../../ui';
import { getSessionUser, hasPermission } from '../../utils/roleAccess';

const BASE_URL = process.env.REACT_APP_BASE_URL;
const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1);

function BusinessSettings() {
  const navigate = useNavigate();
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allowVoucherCreation, setAllowVoucherCreation] = useState(false);
  const [allowVoucherInstallments, setAllowVoucherInstallments] = useState(false);
  const [hasCustomVoucherDate, setHasCustomVoucherDate] = useState(false);
  const [customVoucherDate, setCustomVoucherDate] = useState(null);
  const [popup, setPopup] = useState({ open: false, type: '', message: '' });

  useEffect(() => {
    const sessionStr = sessionStorage.getItem('user-info');
    if (!sessionStr) {
      navigate('/login');
      return;
    }
    const session = JSON.parse(sessionStr);
    const r = (session.user.role || '').toLowerCase();
    if (!['superadmin', 'admin', 'clubadmin'].includes(r)) {
      navigate('/dashboard');
      return;
    }
    setCompanyId(session.user.company_id);
  }, [navigate]);

  useEffect(() => {
    if (!companyId) return;
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}company/${companyId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || 'Failed to load company');
        setAllowVoucherCreation(!!data.allow_voucher_creation);
        setAllowVoucherInstallments(!!data.allow_voucher_installments);
        setHasCustomVoucherDate(!!data.has_custom_voucher_date);
        const day = Number(data.custom_voucher_date);
        setCustomVoucherDate(
          Number.isInteger(day) && day >= 1 && day <= 31 ? day : null
        );
      } catch (e) {
        setPopup({ open: true, type: 'failure', message: e.message || 'Load failed' });
      } finally {
        setLoading(false);
      }
    })();
  }, [companyId]);

  const handleSave = async () => {
    if (hasCustomVoucherDate && (customVoucherDate == null || customVoucherDate < 1 || customVoucherDate > 31)) {
      setPopup({
        open: true,
        type: 'failure',
        message: 'Please select a day of the month (1–31).',
      });
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${BASE_URL}company/${companyId}/business-settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          allow_voucher_creation: allowVoucherCreation,
          allow_voucher_installments: allowVoucherInstallments,
          has_custom_voucher_date: hasCustomVoucherDate,
          custom_voucher_date: hasCustomVoucherDate ? customVoucherDate : null,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Save failed');
      setHasCustomVoucherDate(!!data.has_custom_voucher_date);
      const day = Number(data.custom_voucher_date);
      setCustomVoucherDate(
        Number.isInteger(day) && day >= 1 && day <= 31 ? day : null
      );
      setPopup({ open: true, type: 'success', message: 'Business settings saved.' });
    } catch (e) {
      setPopup({ open: true, type: 'failure', message: e.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !companyId) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ m: 3 }}>
        <PageHeader
          title="Business settings"
          subtitle="Controls optional features for your gym business account."
        />

        <Card elevation={2} sx={{ maxWidth: 560 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Vouchers
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={allowVoucherCreation}
                  onChange={(e) => setAllowVoucherCreation(e.target.checked)}
                  color="primary"
                />
              }
              label="Allow admin voucher creation"
            />
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
              When enabled, authorized staff can create membership or trainer invoices through the admin
              voucher flow.
            </Typography>
            {allowVoucherCreation && (
              <Button
                component={Link}
                to="/admin/vouchers/create"
                variant="outlined"
                size="small"
                sx={{ mr: 2 }}
              >
                Open voucher creation
              </Button>
            )}
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={allowVoucherInstallments}
                  onChange={(e) => setAllowVoucherInstallments(e.target.checked)}
                  color="primary"
                />
              }
              label="Allow splitting vouchers into installments"
            />
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
              When enabled, authorized staff can split an unpaid invoice voucher into multiple
              installment vouchers, each due on the membership end date.
            </Typography>
            <Divider sx={{ my: 2 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={hasCustomVoucherDate}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHasCustomVoucherDate(checked);
                    if (!checked) setCustomVoucherDate(null);
                  }}
                  color="primary"
                />
              }
              label="Use custom voucher day for new members"
            />
            <Typography variant="body2" color="text.secondary" display="block" sx={{ mt: 1, mb: 2 }}>
              When enabled, newly added members start on this day of the month they are added
              (end date is one calendar month later). Existing members are not changed.
            </Typography>
            {hasCustomVoucherDate && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                  Day of month{customVoucherDate != null ? `: ${customVoucherDate}` : ''}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0.75,
                  }}
                >
                  {DAY_OPTIONS.map((day) => {
                    const selected = customVoucherDate === day;
                    return (
                      <Button
                        key={day}
                        type="button"
                        variant={selected ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setCustomVoucherDate(day)}
                        sx={{
                          minWidth: 0,
                          px: 0,
                          py: 0.75,
                          fontWeight: selected ? 700 : 500,
                        }}
                      >
                        {day}
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            )}
            {hasPermission(getSessionUser(), 'settings:update') && (
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? <CircularProgress size={22} /> : 'Save'}
            </Button>
            )}
          </CardContent>
        </Card>
      </Box>

      {popup.open && (
        <Popup
          onClose={() => setPopup({ ...popup, open: false })}
          alertType={popup.type === 'success' ? 'success' : 'failure'}
          message={popup.message}
        />
      )}
    </>
  );
}

export default BusinessSettings;
